<template>
  <div
    ref="wrapper"
    v-headroom="{
      offset,
      classes: { initial: 'headroom headroom--stickyfilterbar' },
    }"
    class="headroom-wrapper"
  >
    <div :style="computedStyle" class="filter-bar">
      <div class="filter-bar__wrapper">
        <div
          :class="{ active: showFilters }"
          class="filter-bar__inner flex flex-nowrap justify-between container"
        >
          <!-- expands filter options on mobile -->
          <div
            :class="{ 'has-selections': hasFilters }"
            class="filter-bar__section filters--toggle"
          >
            <button
              v-show="hasFilters"
              :class="{ active: hasFilters }"
              aria-label="Reset all filters"
              class="button reset"
              @click="resetAllFilters"
            >
              <i class="icon visible-xs">
                <Icon name="fa6-solid:circle-xmark" />
              </i>
              <i class="icon hidden-xs">
                <Icon name="fa6-solid:xmark" />
              </i>
              <span class="hidden-xs"> Reset Filters </span>
            </button>
            <button class="button toggle visible-xs" @click="toggleFilters">
              <span> Filters </span>
              <i class="icon icon--opens">
                <Icon name="fa6-solid:chevron-down" />
              </i>
            </button>
          </div>
          <div
            :class="{ active: showFilters }"
            class="filter-bar__section filters flex"
          >
            <ClientOnly>
              <component
                :is="filter.component"
                v-for="filter in filterComponents"
                :key="filter.key"
                v-model="filters[filter.key]"
                :is-multi-select="isMultiSelect"
                :label="labels[filter.key]"
                :use-singular-term="useSingularTerm"
              />
            </ClientOnly>
          </div>
          <!-- sorting -->
          <div class="filter-bar__section filters--sorting">
            <SortSelect v-model="sort" />
          </div>
        </div>
        <div
          v-show="showFilters"
          class="filter-bar__fill-behind"
          @click="toggleFilters"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { QueryParams } from '~/modules/queryFormatters/DefaultQueryFormatter';

const props = withDefaults(
  defineProps<{
    modelValue?: {
      filters: QueryParams
      sort: { by: string; options: { label: string; value: string }[] }
    }
    isMultiSelect?: boolean
    useSingularTerm?: boolean
    labels?: Record<string, string>
  }>(),
  {
    modelValue: () => ({
      filters: {},
      sort: { by: 'relevance', options: [] },
    }),
    isMultiSelect: true,
    useSingularTerm: false,
    labels: () => ({}),
  },
)
const wrapper = ref<HTMLElement | null>(null)
const offset = ref(66)
const height = ref(0)
const emit = defineEmits(['sort', 'reset'])
const showFilters = ref(false)
const handleResize = useDebounceFn(() => {
  headroomOffset()
}, 300)

function headroomOffset() {
  offset.value =
    wrapper.value?.getBoundingClientRect().top +
    (window.pageYOffset || document.documentElement.scrollTop) -
    66
}

const filters = computed({
  get: () => props.modelValue.filters,
  set: () => {
    // we DON'T need to do anything here,
    // changes will be detected and acted on by the parent automatically
    // as we are directly being passed the parent object
  },
})

const sort = computed({
  get: () => props.modelValue.sort,
  set: (value) => {
    // we DO need to emit a `sort` change here,
    // as we are being passed a computed value,
    // so the parent property needs to be update to recompute the changed value
    emit('sort', value.by)
  },
})

const hasFilters = computed(
  () => !!Object.values(filters.value).find((filter) => filter.length),
)

const computedStyle = computed(() => {
  const updatedHeight =
    wrapper.value?.getBoundingClientRect()?.height > 0
      ? wrapper.value?.getBoundingClientRect()?.height
      : height.value

  return { 'min-height': updatedHeight + 'px' }
})

const filterComponents = computed(() =>
  Object.keys(filters.value)
    .filter((key) => validFilters.includes(key))
    .map((filter) => {
      return {
        key: filter,
        component: `FilterBy` + pascalCase(filter),
      }
    }),
)

function toggleFilters() {
  showFilters.value = !showFilters.value
}

function resetAllFilters() {
  emit('reset')
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  nextTick(() => handleResize())
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="less">
.filter-bar {
  .relative();

  margin: @gutter-xl 0 0;

  .filter-bar__wrapper {
    .relative();

    z-index: 10;
    top: 0;
    left: 0;
    width: 100%;
    background-color: transparent;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0);
    transition:
      background-color 0.6s 0.1s,
      box-shadow 0.6s 0.3s;

    .filter-bar__inner {
      .relative();

      background: @color-fill-normal;

      &.active {
        z-index: 50;
      }

      .filter-bar__section {
        @media @tablet {
          padding: @gutter-xxs 0 @gutter-sm;
        }

        .filter {
          flex: 0 1 100%;
          min-width: 0;

          @media @tablet {
            flex: 1 1 50%;
            padding: 0 @gutter-xxs;
            // // flex-basis: 45%;
          }

          @media @desktop {
            flex-grow: 1;
            flex-basis: 25%;
            max-width: @320px;
          }
        }

        &.filters {
          .hidden();
          .flex-col();
          .flex-no-wrap();
          .absolute();
          .shadow-popover();

          width: 100%;
          background: @color-fill-normal;
          top: 100%;
          left: 0;
          flex: 1 1 auto;
          border-top: @1px solid @color-fill-divide;
          padding-bottom: @48px;

          &.active {
            .flex();
          }

          @media @tablet {
            .flex();
            .flex-row();
            .flex-wrap();
            .static();

            width: auto;
            top: auto;
            left: auto;
            border: 0;
            padding-bottom: @gutter-sm;
            padding-left: @gutter-xxs;
            box-shadow: none;
          }
        }

        &.filters--sorting {
          .block();

          min-width: 0;
          margin-left: auto;

          @media @tablet {
            flex: 0 0 20%;
            padding-right: @gutter-xxs;
          }

          .filter {
            min-width: 0;
          }
        }

        &.filters--toggle {
          flex: 0 0 auto;

          > button {
            .round(0);

            flex: 0 0 auto;
            border: 0;
            background: transparent;
            padding: @gutter-xs @gutter @gutter-xs 0;
            color: @color-text-body;

            @media @tablet {
              padding: @gutter-sm @gutter @gutter-sm 0;
            }

            span {
              .font-size(16, 36);

              padding: 0 @gutter;
            }

            &.reset {
              color: @color-primary;
              padding: 0 @gutter-sm 0 @gutter;

              @media @tablet {
                .absolute();
                .inline-flex() !important;

                z-index: -1;
                bottom: 100%;
                right: 0;
                background: @color-primary;
                border-bottom: @1px solid darken(@color-primary, 5%);
                transition:
                  background 0.3s 0.2s,
                  transform 0.2s cubic-bezier(0, 0.5, 0.66, 1) 0.2s;
                transform: translate(0, 100%);

                i.icon,
                span {
                  color: @color-text-reverse;
                }

                span {
                  .font-sm();
                  .font-medium();
                  .font-body();

                  padding: 0 @gutter-sm;
                }

                &:hover,
                &:active {
                  background: darken(@color-primary, 5%);
                }

                &.active {
                  transition:
                    background 0.3s,
                    transform 0.4s cubic-bezier(0, 0.5, 0.66, 1);
                  transform: translate(0, 0);
                }
              }
            }

            &.toggle {
              .justify-between();

              flex-grow: 1;

              .icon {
                padding: 0;
              }
            }
          }

          &.has-selections {
            button.toggle {
              color: @color-text;
              span {
                padding-left: 0;
              }
            }
          }
        }
      }
    }

    .filter-bar__fill-behind {
      .fixed();

      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 49;
    }
  }
}
</style>

<style lang="less">
// headroom specifics
.headroom-wrapper,
.headroom.headroom--stickyfilterbar {
  height: 100% !important;
}

.headroom.headroom--stickyfilterbar {
  &.headroom--not-top {
    .filter-bar {
      .filter-bar__wrapper {
        .fixed();
        .shadow-popover();

        top: @mobile-nav-height;
        z-index: 50;
        width: 100vw;
        background-color: @color-fill-normal;
        border-top: @1px solid @color-border;

        @media @tablet {
          border: none;
          padding: 0;
        }

        .filter-bar__inner {
          margin: 0 auto 0;

          .filter-bar__section.filters--toggle {
            button.reset {
              @media @tablet {
                display: none !important;
                bottom: 0;
                transform: translate(0, 100%);

                &.active {
                  .inline-flex() !important;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
